/**
 * Paywall content — single source of truth for the upgrade-dialog copy.
 *
 * Maps the user's subscription state to canonical title/description i18n keys
 * so call sites don't hand-roll their own. Payload overrides still take
 * precedence when a feature-specific gate needs custom context (e.g.
 * "Translation is a Pro feature").
 */

import type { UserSubscription } from "../types";

export type PaywallState = "upgrade" | "trial_ending" | "expired";

/** Days remaining at or below which we switch to the trial-ending message. */
const TRIAL_ENDING_THRESHOLD_DAYS = 3;

export function getPaywallState(
  subscription: UserSubscription | null | undefined
): PaywallState {
  if (!subscription) return "upgrade";
  if (subscription.isExpired) return "expired";
  const remainingDays = subscription.remainingDays ?? 0;
  if (
    remainingDays > 0 &&
    remainingDays <= TRIAL_ENDING_THRESHOLD_DAYS
  ) {
    return "trial_ending";
  }
  return "upgrade";
}

interface CopyKeys {
  titleKey: string;
  descKey: string;
}

export const PAYWALL_COPY: Record<PaywallState, CopyKeys> = {
  upgrade: {
    titleKey: "PAYWALL.UPGRADE.TITLE",
    descKey: "PAYWALL.UPGRADE.DESC",
  },
  trial_ending: {
    titleKey: "PAYWALL.TRIAL_ENDING.TITLE",
    descKey: "PAYWALL.TRIAL_ENDING.DESC",
  },
  expired: {
    titleKey: "PAYWALL.EXPIRED.TITLE",
    descKey: "PAYWALL.EXPIRED.DESC",
  },
};
